import React, { useMemo, useState } from 'react'
import { useTheme } from 'styled-components'
import {
  FaChevronDown,
  FaChevronRight,
  FaSearch,
  FaExpandArrowsAlt,
  FaCompressArrowsAlt,
} from 'react-icons/fa'
import {
  ContentCard,
  FilterBar,
  QuadrantBadge,
  StyledTable,
  TableWrapper,
} from './styles'
import { HierarquiaCategoria } from './types'

interface SkuHeatmapMatrixProps {
  hierarquia: HierarquiaCategoria[]
}

const formatMoney = (val: number) => {
  return (val || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  })
}

const formatNumber = (val: number) => {
  return Math.round(val || 0).toLocaleString('pt-BR')
}

export const SkuHeatmapMatrix: React.FC<SkuHeatmapMatrixProps> = ({ hierarquia }) => {
  const theme = useTheme() as any
  const isLight = theme?.name === 'light'
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedCats, setExpandedCats] = useState<Record<string, boolean>>({})
  const [expandedSubs, setExpandedSubs] = useState<Record<string, boolean>>({})

  const toggleCat = (catName: string) => {
    setExpandedCats(prev => ({ ...prev, [catName]: !prev[catName] }))
  }

  const toggleSub = (subKey: string) => {
    setExpandedSubs(prev => ({ ...prev, [subKey]: !prev[subKey] }))
  }

  const handleExpandAll = () => {
    const cats: Record<string, boolean> = {}
    const subs: Record<string, boolean> = {}
    hierarquia.forEach(cat => {
      cats[cat.categoria] = true
      cat.subcategorias?.forEach(sub => {
        subs[`${cat.categoria}||${sub.familia}`] = true
      })
    })
    setExpandedCats(cats)
    setExpandedSubs(subs)
  }

  const handleCollapseAll = () => {
    setExpandedCats({})
    setExpandedSubs({})
  }

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return hierarquia
    const term = searchTerm.toLowerCase().trim()

    return hierarquia
      .map(cat => {
        const catMatch = cat.categoria.toLowerCase().includes(term)
        const matchingSubs = (cat.subcategorias || [])
          .map(sub => {
            const subMatch = sub.familia.toLowerCase().includes(term)
            const matchingSkus = (sub.skus || []).filter(
              sku =>
                sku.sku.toLowerCase().includes(term) ||
                sku.descricao.toLowerCase().includes(term)
            )

            if (subMatch || matchingSkus.length > 0) {
              return {
                ...sub,
                skus: subMatch ? sub.skus : matchingSkus,
              }
            }
            return null
          })
          .filter(Boolean) as any[]

        if (catMatch || matchingSubs.length > 0) {
          return {
            ...cat,
            subcategorias: catMatch ? cat.subcategorias : matchingSubs,
          }
        }
        return null
      })
      .filter(Boolean) as HierarquiaCategoria[]
  }, [hierarquia, searchTerm])

  return (
    <ContentCard>
      <FilterBar>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, color: isLight ? '#0f172a' : '#fff' }}>
            Visão Tabular Hierárquica (Drill-Down de Mix)
          </h3>
          <p style={{ margin: '4px 0 0 0', fontSize: 12, color: isLight ? '#64748b' : '#94a3b8' }}>
            Clique na Categoria para abrir as Subcategorias/Famílias e ver os SKUs individuais
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className='search-wrapper'>
            <FaSearch className='search-icon' />
            <input
              type='text'
              placeholder='Buscar categoria, subcategoria ou SKU...'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          <button
            type='button'
            onClick={handleExpandAll}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)',
              border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.1)',
              color: isLight ? '#334155' : '#cbd5e1',
              padding: '8px 12px',
              borderRadius: 8,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            <FaExpandArrowsAlt size={11} /> Expandir Tudo
          </button>

          <button
            type='button'
            onClick={handleCollapseAll}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)',
              border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.1)',
              color: isLight ? '#334155' : '#cbd5e1',
              padding: '8px 12px',
              borderRadius: 8,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            <FaCompressArrowsAlt size={11} /> Recolher
          </button>
        </div>
      </FilterBar>

      <TableWrapper>
        <StyledTable>
          <thead>
            <tr>
              <th style={{ width: 320 }}>Categoria / Subcategoria / SKU</th>
              <th className='center'>Quadrante</th>
              <th className='right'>SKUs Ativos</th>
              <th className='right'>PDVs Positivados</th>
              <th className='right'>Penetração (%)</th>
              <th className='right'>Média SKUs/PDV</th>
              <th className='right'>Venda Média / PDV</th>
              <th className='right'>Volume (Cx)</th>
              <th className='right'>Faturamento Total</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map(cat => {
              const isCatExpanded = !!expandedCats[cat.categoria] || !!searchTerm.trim()

              return (
                <React.Fragment key={cat.categoria}>
                  <tr className='cat-row' onClick={() => toggleCat(cat.categoria)}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {isCatExpanded ? (
                          <FaChevronDown size={11} color='#a6c10b' />
                        ) : (
                          <FaChevronRight size={11} color='#94a3b8' />
                        )}
                        <span style={{ color: isLight ? '#0f172a' : '#fff', fontWeight: 700 }}>
                          {cat.categoria}
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            color: isLight ? '#475569' : '#94a3b8',
                            background: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.05)',
                            padding: '1px 6px',
                            borderRadius: 4,
                          }}
                        >
                          {cat.subcategorias?.length || 0} subcats
                        </span>
                      </div>
                    </td>
                    <td className='center'>
                      <QuadrantBadge $quad={cat.quadrante}>{cat.quadrante}</QuadrantBadge>
                    </td>
                    <td className='right'>{cat.skusAtivos}</td>
                    <td className='right'>{formatNumber(cat.pdvsPositivados)}</td>
                    <td className='right'>{cat.penetracaoPct}%</td>
                    <td className='right'>{cat.mediaSkusPorPdv?.toFixed(1)}</td>
                    <td className='right' style={{ color: '#a6c10b', fontWeight: 600 }}>
                      {formatMoney(cat.vendaMediaPorSku)}
                    </td>
                    <td className='right'>{formatNumber(cat.caixas)} cx</td>
                    <td className='right' style={{ fontWeight: 700, color: isLight ? '#0f172a' : '#fff' }}>
                      {formatMoney(cat.faturamento)}
                    </td>
                  </tr>

                  {isCatExpanded &&
                    cat.subcategorias?.map(sub => {
                      const subKey = `${cat.categoria}||${sub.familia}`
                      const isSubExpanded = !!expandedSubs[subKey] || !!searchTerm.trim()

                      return (
                        <React.Fragment key={subKey}>
                          <tr className='sub-row' onClick={() => toggleSub(subKey)}>
                            <td>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  paddingLeft: 24,
                                }}
                              >
                                {isSubExpanded ? (
                                  <FaChevronDown size={10} color='#60a5fa' />
                                ) : (
                                  <FaChevronRight size={10} color='#64748b' />
                                )}
                                <span style={{ color: isLight ? '#1e293b' : '#e2e8f0', fontWeight: 600 }}>
                                  {sub.familia}
                                </span>
                                <span
                                  style={{
                                    fontSize: 10,
                                    color: isLight ? '#64748b' : '#64748b',
                                  }}
                                >
                                  ({sub.skus?.length || 0} SKUs)
                                </span>
                              </div>
                            </td>
                            <td className='center'>
                              <QuadrantBadge $quad={sub.quadrante}>{sub.quadrante}</QuadrantBadge>
                            </td>
                            <td className='right'>{sub.skusAtivos}</td>
                            <td className='right'>{formatNumber(sub.pdvsPositivados)}</td>
                            <td className='right'>{sub.penetracaoPct}%</td>
                            <td className='right'>{sub.mediaSkusPorPdv?.toFixed(1)}</td>
                            <td className='right' style={{ color: '#60a5fa' }}>
                              {formatMoney(sub.vendaMediaPorSku)}
                            </td>
                            <td className='right'>{formatNumber(sub.caixas)} cx</td>
                            <td className='right' style={{ color: isLight ? '#1e293b' : 'inherit' }}>{formatMoney(sub.faturamento)}</td>
                          </tr>

                          {isSubExpanded &&
                            sub.skus?.map(sku => (
                              <tr key={sku.sku} className='sku-row'>
                                <td>
                                  <div style={{ paddingLeft: 48, display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ color: isLight ? '#334155' : '#fff', fontWeight: 500 }}>
                                      {sku.descricao}
                                    </span>
                                    <span style={{ fontSize: 10, color: isLight ? '#64748b' : '#64748b' }}>
                                      SKU: {sku.sku} | Marca: {sku.marca}
                                    </span>
                                  </div>
                                </td>
                                <td className='center'>
                                  <span style={{ fontSize: 10, color: '#64748b' }}>-</span>
                                </td>
                                <td className='right'>1</td>
                                <td className='right'>{formatNumber(sku.pdvsPositivados)}</td>
                                <td className='right'>{sku.penetracaoPct}%</td>
                                <td className='right'>-</td>
                                <td className='right' style={{ color: isLight ? '#475569' : '#94a3b8' }}>
                                  {formatMoney(sku.faturamento)}
                                </td>
                                <td className='right'>{formatNumber(sku.caixas)} cx</td>
                                <td className='right' style={{ color: isLight ? '#334155' : 'inherit' }}>{formatMoney(sku.faturamento)}</td>
                              </tr>
                            ))}
                        </React.Fragment>
                      )
                    })}
                </React.Fragment>
              )
            })}
          </tbody>
        </StyledTable>
      </TableWrapper>
    </ContentCard>
  )
}
